import type { TranslationTable } from "@repo/shared/types";

/**
 * Localized default titles and descriptions for flow components
 */

// Localized component defaults (titles and descriptions)
export const T: TranslationTable<Record<string, { title: string; description: string }>> = {
  en_GB: {
    goal_input: {
      title: "Goal Details",
      description: "Enter the details for this investment goal, including its name and type.",
    },
    portfolio_input: {
      title: "Portfolio Details",
      description:
        "Enter the details for this portfolio, including its name, amount, and investment schedule.",
    },
    custom_portfolio: {
      title: "Custom Portfolio",
      description:
        "Build a custom portfolio by selecting products and specifying investment amounts.",
    },
    client_classification: {
      title: "Client Classification",
      description:
        "Answer the questions below to determine your client classification under MiFID II regulations.",
    },
    product_assessment: {
      title: "Product Assessment",
      description:
        "Tell us about your knowledge and experience with each type of investment. This helps us recommend suitable products for your portfolio.",
    },
    wealth_plan: {
      title: "Wealth Plan",
      description:
        "Plan future withdrawals from your investment portfolio to meet your financial goals.",
    },
    account_cash_overview: {
      title: "Account cash overview",
      description: "View account balances and planned cash movements.",
    },
    cash_transfers: {
      title: "Cash transfers",
      description: "Add, edit and delete transfers in this flow.",
    },
    goal_list: {
      title: "Goal list",
      description: "Enter the goals that are relevant for this flow instance.",
    },
    financial_situation: {
      title: "Financial Situation",
      description:
        "Please provide information about your income, assets, liabilities, and regular commitments.",
    },
    sustainability: {
      title: "Sustainability Preferences",
      description: "Tell us whether sustainability matters to you and what your preferences are.",
    },
    sustainability_status: {
      title: "Sustainability Status",
      description: "How the portfolio's products match your sustainability preferences.",
    },
    client_preferences: {
      title: "Client Preferences",
      description: "Tell us what you want and don't want in your investment products.",
    },
    quiz: {
      title: "Quiz",
      description: "Answer a few questions to show you understand what you're investing in.",
    },
    trapets_lookup: {
      title: "PEP/Sanction Check",
      description:
        "Verify the client against PEP (Politically Exposed Person) and sanctions databases.",
    },
    sumsub_verification: {
      title: "Sumsub Identity Verification",
      description: "Verify the client's identity using document verification and biometric checks.",
    },
    enin_lookup: {
      title: "Company Lookup",
      description: "Look up the company's registry data, financials and risk flags via Enin.",
    },
    risk_assessment: {
      title: "Risk Assessment",
      description:
        "Answer a few questions to help us understand your investment time horizon and risk tolerance.",
    },
    portfolio_display: {
      title: "Portfolio Allocation",
      description: "Based on your risk profile, here is your recommended portfolio allocation.",
    },
    portfolio_xray: {
      title: "Portfolio Analysis",
      description: "Detailed breakdown of your portfolio's underlying holdings and exposures.",
    },
    asset_allocation: {
      title: "Asset Allocation",
      description: "How your investment is distributed across different asset classes.",
    },
    asset_class_forecast: {
      title: "Asset Class Forecast",
      description: "How the mix across asset classes is expected to change over time.",
    },
    volatility_chart: {
      title: "Volatility Chart",
      description: "How portfolio volatility changes over time relative to the allowed limit.",
    },
    equity_share_chart: {
      title: "Equity Share Chart",
      description:
        "How the portfolio's equity share changes over time relative to the allowed limit.",
    },
    mandate: {
      title: "Mandate",
      description: "The permitted allocation for this goal, and where the advice lands against it.",
    },
    mandate_comparison: {
      title: "Mandate comparison",
      description: "The client's current mandate beside the one this advice proposes.",
    },
    portfolio_forecast: {
      title: "Portfolio Forecast",
      description:
        "Projected portfolio value over time with 95% confidence bounds based on historical market data.",
    },
    historical_return: {
      title: "Historical Returns",
      description:
        "Historical performance of your portfolio based on past market data. Past performance is not indicative of future results.",
    },
    expected_value: {
      title: "Expected Value",
      description:
        "Projected portfolio value over time based on expected returns and your investment schedule.",
    },
    cost_table: {
      title: "Cost Summary",
      description:
        "Breakdown of all costs associated with your investment, including management fees and product costs.",
    },
    cost_chart: {
      title: "Cost Impact",
      description: "Visualization of how costs affect your portfolio value over time.",
    },
    portfolio_costs: {
      title: "Advisory Fees",
      description: "Platform and advisory fees applicable to your investment portfolio.",
    },
    product_costs: {
      title: "Product Costs",
      description:
        "Ongoing costs for each product in your portfolio, including expense ratios and transaction fees.",
    },
    cost_benefit: {
      title: "Cost Benefit",
      description:
        "How the proposed portfolio compares with your existing one on expected return, risk and costs, and what switching costs one time.",
    },
    portfolio_list: {
      title: "Portfolios",
      description: "Overview of your investment portfolios and their payment schedules.",
    },
  },
  nb_NO: {
    goal_input: {
      title: "Måldetaljer",
      description: "Skriv inn detaljene for dette investeringsmålet, inkludert navn og type.",
    },
    portfolio_input: {
      title: "Porteføljedetaljer",
      description:
        "Skriv inn detaljene for denne porteføljen, inkludert navn, beløp og investeringsplan.",
    },
    custom_portfolio: {
      title: "Tilpasset portefølje",
      description:
        "Bygg en tilpasset portefølje ved å velge produkt og spesifisere investeringsbeløp.",
    },
    client_classification: {
      title: "Kundeklassifisering",
      description:
        "Svar på spørsmålene nedenfor for å fastsette din kundeklassifisering i henhold til MiFID II-regelverket.",
    },
    product_assessment: {
      title: "Produktvurdering",
      description:
        "Fortell oss om din kunnskap og erfaring med hver investeringstype. Dette hjelper oss med å anbefale passende produkter for din portefølje.",
    },
    wealth_plan: {
      title: "Formuesplan",
      description:
        "Planlegg fremtidige uttak fra investeringsporteføljen din for å nå dine økonomiske mål.",
    },
    account_cash_overview: {
      title: "Kontantoversikt per konto",
      description: "Se kontosaldoer og planlagte kontantbevegelser.",
    },
    cash_transfers: {
      title: "Kontantoverføringer",
      description: "Legg til, rediger og slett overføringer i denne flyten.",
    },
    goal_list: {
      title: "Målliste",
      description: "Legg inn målene som er relevante for denne flyten.",
    },
    financial_situation: {
      title: "Finansiell situasjon",
      description:
        "Vennligst oppgi informasjon om din inntekt, eiendeler, gjeld og faste forpliktelser.",
    },
    sustainability: {
      title: "Bærekraftspreferanser",
      description: "Fortell oss om bærekraft er viktig for deg og hva preferansene dine er.",
    },
    sustainability_status: {
      title: "Bærekraftsstatus",
      description: "Hvordan porteføljens produkter matcher bærekraftspreferansene dine.",
    },
    client_preferences: {
      title: "Kundepreferanser",
      description: "Fortell oss hva du ønsker og ikke ønsker i investeringsproduktene dine.",
    },
    quiz: {
      title: "Quiz",
      description: "Svar på noen spørsmål for å vise at du forstår hva du investerer i.",
    },
    trapets_lookup: {
      title: "PEP/Sanksjonssjekk",
      description: "Verifiser kunden mot PEP (politisk eksponert person) og sanksjonsdatabaser.",
    },
    sumsub_verification: {
      title: "Sumsub Identitetsverifisering",
      description:
        "Verifiser kundens identitet ved hjelp av dokumentverifisering og biometriske kontroll.",
    },
    enin_lookup: {
      title: "Selskapsoppslag",
      description: "Slå opp selskapets registerdata, regnskap og risikoflagg via Enin.",
    },
    risk_assessment: {
      title: "Risikovurdering",
      description:
        "Svar på noen spørsmål for å hjelpe oss med å forstå din investeringshorisont og risikotoleranse.",
    },
    portfolio_display: {
      title: "Porteføljeallokering",
      description: "Basert på din risikoprofil, her er din anbefalte porteføljeallokering.",
    },
    portfolio_xray: {
      title: "Porteføljeanalyse",
      description:
        "Detaljert oversikt over porteføljens underliggende beholdninger og eksponeringer.",
    },
    asset_allocation: {
      title: "Aktivaallokering",
      description: "Hvordan investeringen din er fordelt på ulike aktivaklasser.",
    },
    asset_class_forecast: {
      title: "Aktivaklasseprognose",
      description: "Hvordan fordelingen mellom aktivaklasser forventes å endre seg over tid.",
    },
    volatility_chart: {
      title: "Volatilitetsdiagram",
      description:
        "Hvordan porteføljevolatiliteten endrer seg over tid i forhold til tillatt grense.",
    },
    equity_share_chart: {
      title: "Aksjeandelsdiagram",
      description:
        "Hvordan porteføljens aksjeandel endrer seg over tid i forhold til tillatt grense.",
    },
    mandate: {
      title: "Mandat",
      description:
        "Den tillatte allokeringen for dette målet, og hvor rådet lander i forhold til den.",
    },
    mandate_comparison: {
      title: "Mandatsammenligning",
      description: "Kundens nåværende mandat ved siden av det dette rådet foreslår.",
    },
    portfolio_forecast: {
      title: "Porteføljeprognose",
      description:
        "Forventet porteføljeverdi over tid med 95% konfidensintervall basert på historiske markedsdata.",
    },
    historical_return: {
      title: "Historisk avkastning",
      description:
        "Historisk utvikling av porteføljen basert på tidligere markedsdata. Historisk avkastning er ingen garanti for fremtidig avkastning.",
    },
    expected_value: {
      title: "Forventet verdi",
      description:
        "Forventet porteføljeverdi over tid basert på forventet avkastning og din investeringsplan.",
    },
    cost_table: {
      title: "Kostnadsoversikt",
      description:
        "Oversikt over alle kostnader knyttet til investeringen din, inkludert forvaltningshonorar og produktkostnader.",
    },
    cost_chart: {
      title: "Kostnadseffekt",
      description: "Visualisering av hvordan kostnader påvirker porteføljeverdien over tid.",
    },
    portfolio_costs: {
      title: "Rådgivningskostnader",
      description: "Plattform- og rådgivningskostnader for investeringsporteføljen din.",
    },
    product_costs: {
      title: "Produktkostnader",
      description:
        "Løpende kostnader for hvert produkt i porteføljen din, inkludert forvaltningshonorar og transaksjonskostnader.",
    },
    cost_benefit: {
      title: "Kost/nytte",
      description:
        "Hvordan den foreslåtte porteføljen står seg mot din eksisterende på forventet avkastning, risiko og kostnader, og hva byttet koster én gang.",
    },
    portfolio_list: {
      title: "Porteføljer",
      description: "Oversikt over investeringsporteføljene dine og deres betalingsplaner.",
    },
  },
};
